<template>
  <div class="user-management">
    <h2>User Management</h2>
    
    <!-- Search Form -->
    <div class="search-section">
      <div class="search-type-selection">
        <label>Search By:</label>
        <div class="radio-group">
          <label>
            <input type="radio" v-model="searchType" value="firstName" />
            First Name
          </label>
          <label>
            <input type="radio" v-model="searchType" value="lastName" />
            Last Name
          </label>
          <label>
            <input type="radio" v-model="searchType" value="email" />
            Email
          </label>
          <label>
            <input type="radio" v-model="searchType" value="college" />
            College
          </label>
        </div>
      </div>
      
      <div class="search-form">
        <input 
          type="text" 
          v-model="searchTerm" 
          class="search-input" 
          :placeholder="`Search users by ${searchTypeLabel}...`"
          @keyup.enter="searchUsers"
        />
        <button @click="searchUsers" class="search-button">Search</button>
        <button @click="resetSearch" class="reset-button">Reset</button>
      </div>
    </div>
    
    <!-- Users Table -->
    <div class="users-section">
      <div v-if="isLoading" class="loading">
        <p>Loading users...</p>
      </div>
      
      <div v-else-if="users.length === 0" class="no-users">
        <p v-if="hasSearched">No users found for your search criteria.</p>
        <p v-else>Search for users using the form above.</p>
      </div>
      
      <div v-else class="users-table-container">
        <div class="table-actions">
          <button 
            :disabled="selectedUsers.length === 0" 
            @click="goToGroupAssignment" 
            class="assign-group-button"
          >
            Assign Selected Users to Group
          </button>
          <p v-if="selectedUsers.length > 0" class="selection-info">
            {{ selectedUsers.length }} user(s) selected
          </p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>
                <input 
                  type="checkbox" 
                  :checked="isAllSelected" 
                  @change="toggleAllSelection"
                />
              </th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>College</th>
              <th>Group</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.email">
              <td>
                <input 
                  type="checkbox" 
                  :value="user.email" 
                  v-model="selectedUsers"
                />
              </td>
              <td>{{ user.firstName }}</td>
              <td>{{ user.lastName }}</td>
              <td>{{ user.email }}</td>
              <td>{{ user.college }}</td>
              <td>{{ user.group || 'No Group' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- Group Assignment Modal -->
    <div v-if="showGroupModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Assign Users to Group</h3>
          <button @click="closeGroupModal" class="close-button">&times;</button>
        </div>
        
        <div class="modal-body">
          <p>Select a group to assign {{ selectedUsers.length }} user(s)</p>
          
          <div class="search-form">
            <input 
              type="text" 
              v-model="groupSearchTerm" 
              class="search-input" 
              placeholder="Search for a group..."
              @keyup.enter="searchForGroups"
            />
            <button @click="searchForGroups" class="search-button">Search</button>
          </div>
          
          <div v-if="isGroupLoading" class="loading">
            <p>Loading groups...</p>
          </div>
          
          <div v-else-if="groups.length === 0" class="no-groups">
            <p>No groups found. Please create a group first.</p>
          </div>
          
          <div v-else class="groups-list">
            <div 
              v-for="group in groups" 
              :key="group.groupName"
              :class="['group-item', { selected: selectedGroup === group.groupName }]"
              @click="selectGroup(group.groupName)"
            >
              <div class="group-name">{{ group.groupName }}</div>
              <div class="group-description">{{ group.description || 'No description' }}</div>
            </div>
          </div>
          
          <div class="modal-actions">
            <button 
              @click="assignToGroup" 
              :disabled="!selectedGroup || isAssigning" 
              class="assign-button"
            >
              Assign to Group
            </button>
            <button @click="closeGroupModal" class="cancel-button">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUsers, searchUsers, getGroups, searchGroups, assignUsersToGroup } from '../../services/adminService'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      selectedUsers: [],
      searchType: 'firstName', // Default to first name search
      searchTerm: '',
      isLoading: false,
      hasSearched: false,
      error: null,
      
      // Group assignment modal
      showGroupModal: false,
      groups: [],
      selectedGroup: null,
      groupSearchTerm: '',
      isGroupLoading: false,
      isAssigning: false
    }
  },
  computed: {
    searchTypeLabel() {
      const labels = {
        firstName: 'first name',
        lastName: 'last name',
        email: 'email',
        college: 'college'
      }
      return labels[this.searchType] || 'first name'
    },
    isAllSelected() {
      return this.users.length > 0 && this.selectedUsers.length === this.users.length
    }
  },
  methods: {
    async searchUsers() {
      if (!this.searchTerm.trim()) {
        this.users = []
        this.hasSearched = false
        return
      }
      
      this.isLoading = true
      this.hasSearched = true
      this.selectedUsers = []
      
      try {
        const response = await searchUsers(this.searchType, this.searchTerm)
        this.users = response.users || []
      } catch (error) {
        console.error('Error searching users:', error)
        this.error = 'Failed to search users'
      } finally {
        this.isLoading = false
      }
    },
    
    resetSearch() {
      this.searchTerm = ''
      this.users = []
      this.selectedUsers = []
      this.hasSearched = false
    },
    
    toggleAllSelection() {
      if (this.isAllSelected) {
        this.selectedUsers = []
      } else {
        this.selectedUsers = this.users.map(user => user.email)
      }
    },
    
    goToGroupAssignment() {
      if (this.selectedUsers.length === 0) return
      
      this.showGroupModal = true
      this.loadGroups()
    },
    
    closeGroupModal() {
      this.showGroupModal = false
      this.selectedGroup = null
      this.groupSearchTerm = ''
      this.groups = []
    },
    
    async loadGroups() {
      this.isGroupLoading = true
      
      try {
        const response = await getGroups()
        this.groups = response.groups || []
      } catch (error) {
        console.error('Error loading groups:', error)
        this.error = 'Failed to load groups'
      } finally {
        this.isGroupLoading = false
      }
    },
    
    async searchForGroups() {
      if (!this.groupSearchTerm.trim()) {
        this.loadGroups()
        return
      }
      
      this.isGroupLoading = true
      
      try {
        const response = await searchGroups(this.groupSearchTerm)
        this.groups = response.groups || []
      } catch (error) {
        console.error('Error searching groups:', error)
        this.error = 'Failed to search groups'
      } finally {
        this.isGroupLoading = false
      }
    },
    
    selectGroup(groupName) {
      this.selectedGroup = groupName
    },
    
    async assignToGroup() {
      if (!this.selectedGroup || this.selectedUsers.length === 0) return
      
      this.isAssigning = true
      
      try {
        await assignUsersToGroup(this.selectedUsers, this.selectedGroup)
        
        // Update the group field in the user list
        this.users = this.users.map(user => {
          if (this.selectedUsers.includes(user.email)) {
            return { ...user, group: this.selectedGroup }
          }
          return user
        })
        
        // Close the modal and reset
        this.closeGroupModal()
        this.selectedUsers = []
      } catch (error) {
        console.error('Error assigning users to group:', error)
        this.error = 'Failed to assign users to group'
      } finally {
        this.isAssigning = false
      }
    }
  }
}
</script>

<style scoped>
.user-management {
  width: 100%;
}

h2 {
  margin-bottom: 1.5rem;
  color: var(--secondary);
}

/* Search section */
.search-section {
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.search-type-selection {
  margin-bottom: 1rem;
}

.search-type-selection label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
}

.search-form {
  display: flex;
  gap: 0.5rem;
}

.search-input {
  flex-grow: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* Buttons */
button {
  cursor: pointer;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.2s;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-button {
  background-color: var(--primary);
  color: white;
}

.reset-button {
  background-color: #f0f0f0;
  color: #333;
}

.assign-group-button {
  background-color: var(--secondary);
  color: white;
}

/* Users table */
.users-section {
  margin-top: 1.5rem;
}

.loading, .no-users {
  text-align: center;
  padding: 2rem;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.table-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.selection-info {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
}

.users-table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

th {
  background-color: #f4f4f4;
  font-weight: 600;
}

tr:hover {
  background-color: #f9f9f9;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: var(--secondary);
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.modal-body {
  padding: 1.5rem;
}

.groups-list {
  margin-top: 1rem;
  max-height: 300px;
  overflow-y: auto;
}

.group-item {
  padding: 0.75rem;
  border: 1px solid #eee;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.group-item:hover {
  background-color: #f9f9f9;
}

.group-item.selected {
  background-color: #e6f7ff;
  border-color: #91d5ff;
}

.group-name {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.group-description {
  font-size: 0.9rem;
  color: #666;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

.assign-button {
  background-color: #2ecc71;
  color: white;
}

.cancel-button {
  background-color: #f0f0f0;
  color: #333;
}
</style> 